import { Key, UserPlus, Users } from "lucide-react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { useEffect, useState } from "react";
import Pageable from "../model/page/Pageable";
import Usuario from "../model/Usuario";
import { useToastContext } from "../provider/ToastContext";
import UsuarioService from "../service/administracao/UsuarioService";
import { formatarCnpj, formatarCpf } from "../utils/convert";
import { filterApplyTemplate, filterClearTemplate, TextMatchModeOptions } from "../utils/filterUtils";
import ResetSenhaDialog from "./dialog/ResetSenhaDialog";
import InserirUsuarioDialog from "./dialog/InserirUsuarioDialog";
import { Dialog } from "primereact/dialog";

const defaultFilters: DataTableFilterMeta = {
  documento: {
    constraints: [
      {
        value: '',
        matchMode: FilterMatchMode.STARTS_WITH,
      }
    ],
    operator: FilterOperator.AND
  },
  nome: {
    constraints: [
      {
        value: '',
        matchMode: FilterMatchMode.STARTS_WITH,
      }
    ],
    operator: FilterOperator.AND
  },
  email: {
    constraints: [
      {
        value: '',
        matchMode: FilterMatchMode.STARTS_WITH,
      }
    ],
    operator: FilterOperator.AND
  },

}

const pageSize = 10;
const usuarioService = new UsuarioService();
export default function UsuariosComponent() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [pageable, setPageable] = useState(new Pageable(0, pageSize, "nome,asc", 0));
  const [totalElements, setTotalElements] = useState<number>(0);
  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);

  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showInserirDialog, setShowInserirDialog] = useState(false);
  const [linkTrocaSenhaDialog, setLinkTrocaSenhaDialog] = useState({ link: "", visible: false });
  const { msgSucesso, msgErro } = useToastContext();

  function load() {
    usuarioService.pesquisar(pageable, filters).then((data) => {
      setUsuarios(data.content);
      setTotalElements(data.totalElements);
    }).catch((err) => {
      msgErro("Erro ao carregar usuários", err?.data?.message || "Tente novamente mais tarde.");
    });
  }

  useEffect(() => {
    load();
  }, [pageable, filters])

  const onPage = (event: DataTableStateEvent) => {
    setPageable({ ...pageable, page: event.page !== undefined ? event.page : 0, first: event.first });
  };

  const handleResetSenha = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setShowResetDialog(true);
  };

  const handleGerarLink = (usuario: Usuario) => {
    usuarioService.gerarLinkTrocaSenha(usuario.id).then((data) => {
      setLinkTrocaSenhaDialog({ link: data, visible: true });
    }).catch((err) => {
      msgErro("Erro ao gerar link", err?.data?.message || "Tente novamente mais tarde.");
    });
  };

  const confirmResetSenha = () => {
    if (selectedUsuario) {
      msgSucesso("Senha Resetada", `Uma nova senha foi enviada para ${selectedUsuario.email}.`);
    }
    setShowResetDialog(false);
    setSelectedUsuario(null);
  };

  const documentoTemplate = (rowData: Usuario) => {
    return <span style={{ fontFamily: "monospace" }}>{rowData.cpf ? formatarCpf(rowData.cpf) : formatarCnpj(rowData.cnpj)}</span>;
  };

  const acoesTemplate = (rowData: Usuario) => {
    return (
      <div style={{ display: "flex", columnGap: "8px" }}>
        <Button
          icon={<Key size={16} />}
          label="Gerar link"
          className="p-button-outlined p-button-sm p-button-warning"
          onClick={() => handleGerarLink(rowData)}
        />
        <Button
          icon={<Key size={16} />}
          label="Resetar Senha"
          className="p-button-outlined p-button-sm p-button-warning"
          onClick={() => handleResetSenha(rowData)}
        />
      </div>
    );
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div className="flex align-items-center gap-2">
        <Users size={24} style={{ color: "var(--primary-color)" }} />
        <h5 className="m-0">Gerenciamento de Usuários</h5>
      </div>
      <Button label="Adicionar Usuário" icon={<UserPlus />} outlined className="p-button-primary" onClick={() => setShowInserirDialog(true)} />
    </div>
  );

  return (
    <div>
      <ResetSenhaDialog visible={showResetDialog} onHide={() => setShowResetDialog(false)} usuario={selectedUsuario} onConfirm={() => confirmResetSenha()} />
      <InserirUsuarioDialog visible={showInserirDialog} onHide={() => setShowInserirDialog(false)} onConfirm={() => {
        load();
        setShowInserirDialog(false)
      }} />
      <Dialog visible={linkTrocaSenhaDialog.visible} onHide={() => setLinkTrocaSenhaDialog({ link: "", visible: false })} header="Link para troca de senha">
        <p>Compartilhe o link abaixo com o usuário para que ele possa trocar a senha:</p>
        <div style={{ display: "flex", columnGap: 8 }}>
          <div className="p-card p-component" >
            <div className="p-card-body">
              <code>{linkTrocaSenhaDialog.link}</code>
            </div>
          </div>
          <Button label="Copiar link" className="p-button-sm mt-3" onClick={() => {
            navigator.clipboard.writeText(linkTrocaSenhaDialog.link);
            msgSucesso("Link copiado", "O link para troca de senha foi copiado para a área de transferência.");
          }} />
        </div>

      </Dialog>

      <div className="mb-4">
        <h2>Usuários</h2>
        <p className="text-muted">Gerencie os usuários do sistema</p>
      </div>

      <Card>
        <DataTable
          value={usuarios} paginator rows={pageSize}
          first={pageable.first} totalRecords={totalElements} onPage={onPage}
          header={header}
          filters={filters}
          onFilter={(e) => {
            setFilters(e.filters);
          }} lazy dataKey="id"
          stripedRows
          emptyMessage="Nenhum usuário encontrado."
        >
          <Column header="Documento" filterField="documento"
            filter showFilterOperator={false} showAddButton={false} filterPlaceholder="Escreva o filtro"
            filterMatchModeOptions={TextMatchModeOptions} filterApply={filterApplyTemplate} filterClear={filterClearTemplate}
            body={documentoTemplate} />
          <Column field="nome" header="Nome"
            filter showFilterOperator={false} showAddButton={false} filterPlaceholder="Escreva o filtro"
            filterMatchModeOptions={TextMatchModeOptions} filterApply={filterApplyTemplate} filterClear={filterClearTemplate} />
          <Column field="email" header="Email"
            filter showFilterOperator={false} showAddButton={false} filterPlaceholder="Escreva o filtro"
            filterMatchModeOptions={TextMatchModeOptions} filterApply={filterApplyTemplate} filterClear={filterClearTemplate} />
          <Column
            header="Ações"
            body={acoesTemplate}
            style={{ textAlign: "right", width: "350px" }}
          />
        </DataTable>
      </Card>


    </div>
  );
}